import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme, getThemeShapeClasses, getThemeBorderRadius } from "../utils/themeUtils"
import { buildServiceUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const FeaturedServicesSectionDesktop = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "double_img_r"
  const selectedServiceIds = settings.selectedServiceIds || []

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium, bgExtraLight, or bgLight are missing, generate them from primary color
    if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
      const primaryColor = colors.primary
      if (primaryColor) {
        const generatedPalette = generateColorPalette(primaryColor)
        colors.bgMedium = colors.bgMedium || generatedPalette.bgMedium
        colors.bgExtraLight = colors.bgExtraLight || generatedPalette.bgExtraLight
        colors.bgLight = colors.bgLight || generatedPalette.bgLight
      }
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled (handle both boolean and string values)
    const showBgColor = settings.showBackgroundColor === true || settings.showBackgroundColor === "true"
    const bgColorIsTheme = settings.backgroundColor === "theme"

    if (showBgColor && bgColorIsTheme) {
      // Use bgLight from theme colors
      return themeColors.bgLight || theme?.colors?.bgLight || "#FFFFFF"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  const sectionBackgroundColor = resolveSectionBackgroundColor()
  const headingColor = theme?.colors?.textDark || "#55433F"
  const bodyColor = theme?.colors?.textDark || "#55433F"

  const handleServiceClick = (serviceId) => {
    window.location.href = buildServiceUrl(serviceId, practice)
  }

  // Get practice services - filter to only show public services
  const allPracticeServices = (practice?.services || []).filter((service) => service.public === true)

  // If specific services are selected, use those in the specified order; otherwise use first 2-3 based on layout
  const selectedServices =
    selectedServiceIds.length > 0
      ? selectedServiceIds.map((id) => allPracticeServices.find((service) => service.id === id)).filter(Boolean)
      : allPracticeServices

  // Limit items based on layout
  const maxItems = layout === "list" ? 3 : layout === "highlights_2" ? 2 : layout === "highlights_3" ? 3 : 2
  const allItems = selectedServices.slice(0, maxItems)

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    const currencySuffix = currency === "CAD" ? " CAD" : ""
    if (amountCents % 100 === 0) {
      return `$${amount.toFixed(0)}${currencySuffix}`
    }
    return `$${amount.toFixed(2)}${currencySuffix}`
  }

  // Get service images for double image layouts (first 2 services)
  const getDoubleImageUrls = () => {
    const services = selectedServices.slice(0, 2)
    return services.map(
      (service) =>
        service.imageUrl ||
        service.mediumWebpUrl ||
        (service.photoHandles?.large?.webp
          ? `https://cdn.filestackcontent.com/${service.photoHandles.large.webp}`
          : null) ||
        (service.photoHandles?.original?.any
          ? `https://cdn.filestackcontent.com/${service.photoHandles.original.any}`
          : null) ||
        placeholderImage
    )
  }

  const imageUrls = getDoubleImageUrls()

  // Render service card for highlights layout
  const renderHighlightCard = (item, index) => {
    const price =
      typeof item.amountCents === "number"
        ? item.amountCents === 0
          ? "Free"
          : formatPrice(item.amountCents, practice?.defaultCurrency || "USD")
        : ""
    const duration = item.timeLength ? `${item.timeLength} min` : ""

    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any
        ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}`
        : null) ||
      placeholderImage

    const displayDescription =
      item.description && item.description.length > 200
        ? item.description.substring(0, 200) + "..."
        : item.description || ""

    // Check if this is a package or group event (for tag display)
    const isPackage = item.type === "package"
    const isGroupEvent = item.type === "group_event"

    return (
      <div key={index} className="flex w-full max-w-[560px] flex-col gap-4">
        {/* Image */}
        <div className={twMerge("aspect-[396/250] w-full overflow-hidden", getThemeShapeClasses(theme))}>
          <img
            src={imageUrl}
            alt={item.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-2">
          {/* Price + Title */}
          <div className="flex flex-col gap-0">
            {/* Price and Tag */}
            <div className="flex items-center gap-1.5">
              {price && (
                <span
                  className="text-xl font-semibold"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                    color: headingColor,
                    lineHeight: "32px"
                  }}>
                  {price}
                </span>
              )}
              {(isPackage || isGroupEvent) && (
                <div
                  className="rounded px-3 py-1"
                  style={{
                    backgroundColor: theme?.colors?.bgLight || themeColors.bgLight || "#F4ECE9",
                    borderRadius: theme?.borderRadius ? `${parseInt(theme.borderRadius) / 4}px` : "4px"
                  }}>
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                      color: headingColor,
                      lineHeight: "16px"
                    }}>
                    {isPackage ? "4 Session package" : "Group Event"}
                  </span>
                </div>
              )}
            </div>
            {/* Title */}
            <h3
              className="text-[27px] font-bold leading-[40px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: headingColor
              }}>
              {item.name}
            </h3>
          </div>

          {/* Duration and Location */}
          {(duration || locationInfo) && (
            <p
              className="text-base"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "24px"
              }}>
              {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
            </p>
          )}

          {/* Description - Limited to 2 lines */}
          {displayDescription && (
            <div
              className="line-clamp-2 text-lg"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "32px",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}
            />
          )}

          {/* Details button */}
          <button
            onClick={() => handleServiceClick(item.id)}
            className="inline-flex w-auto items-center justify-center gap-3 self-start border-2 px-10 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: "#FFFFFF",
                borderColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.primary || "#BD654F",
                fontSize: "16px",
                lineHeight: "24px"
              },
              theme
            )}>
            Details
          </button>
        </div>
      </div>
    )
  }

  // Render double image layout
  const renderDoubleImageLayout = (imagesOnRight) => {
    // Calculate the total height of the image group to properly center it
    // First image: starts at 0, height 309.498px, ends at 309.498px
    // Second image: starts at 199.056px, height 340.944px, ends at 540px
    // Total bounding box height: max(309.498, 540) = 540px
    const imageGroupHeight = 540

    return (
      <div className={twMerge("flex w-full items-center", imagesOnRight ? "gap-[104px]" : "justify-start gap-[52px]")}>
        {/* Images Section */}
        <div
          className={twMerge("relative shrink-0", imagesOnRight ? "order-2" : "order-1")}
          style={{ height: `${imageGroupHeight}px` }}>
          <div className="relative h-full">
            {/* First image - positioned behind/offset */}
            {imageUrls[0] && (
              <div
                className="absolute"
                style={{
                  left: imagesOnRight ? "233.196px" : "0",
                  top: "0",
                  width: "278.804px",
                  height: "309.498px"
                }}>
                <img
                  src={imageUrls[0]}
                  alt=""
                  className={twMerge("h-full w-full", getThemeShapeClasses(theme))}
                  style={{
                    objectPosition: "center",
                    transform: imagesOnRight ? "rotate(180deg) scaleY(-1)" : "none",
                    borderRadius: getThemeBorderRadius(theme)
                  }}
                  onError={(e) => {
                    if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                  }}
                />
              </div>
            )}
            {/* Second image - positioned in front/overlapping */}
            {imageUrls[1] && (
              <div
                className="absolute"
                style={{
                  left: imagesOnRight ? "0" : "77.452px", // For left layout, offset second image to the right (356.256 - 278.804 = 77.452)
                  top: "199.056px",
                  width: "356.256px",
                  height: "340.944px"
                }}>
                <img
                  src={imageUrls[1]}
                  alt=""
                  className={twMerge("h-full w-full overflow-hidden", getThemeShapeClasses(theme))}
                  style={{
                    objectPosition: "center"
                  }}
                  onError={(e) => {
                    if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Text Content Section - Removed headline, body, and CTA button */}
        <div
          className={twMerge(
            "flex max-w-[500px] flex-1 flex-col justify-center",
            imagesOnRight ? "order-1" : "order-1"
          )}
          style={imagesOnRight ? {} : { marginLeft: "500px" }}></div>
      </div>
    )
  }

  // Render section header (title, subtitle and Browse Services button)
  const renderSectionHeader = () => {
    const title = settings.title || "Featured Services"
    const subtitle = settings.subtitle || ""

    return (
      <div className="mb-12 flex w-full items-start justify-between gap-10">
        <div className="flex flex-col gap-4">
          {/* Title */}
          <h2
            className="text-left text-5xl font-bold leading-[64px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor
            }}>
            {title}
          </h2>
          {/* Subtitle */}
          {subtitle && (
            <p
              className="text-left text-xl leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor
              }}>
              {subtitle}
            </p>
          )}
        </div>

        {/* Browse Services Button */}
        <button
          onClick={() => (window.location.href = `/${practice?.slug || ""}/services`)}
          className="inline-flex shrink-0 items-center justify-center gap-3 self-center px-10 py-3 transition-opacity hover:opacity-90"
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: theme?.colors?.bgLight || "#F4ECE9",
              fontSize: "16px",
              lineHeight: "24px"
            },
            theme
          )}>
          Browse Services
        </button>
      </div>
    )
  }

  // Render service card for list layout (horizontal row design)
  const renderListServiceCard = (item, index) => {
    const price =
      typeof item.amountCents === "number"
        ? item.amountCents === 0
          ? "FREE"
          : formatPrice(item.amountCents, practice?.defaultCurrency || "USD")
        : ""

    const duration = item.timeLength ? `${item.timeLength} min` : ""
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any
        ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}`
        : null) ||
      placeholderImage

    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    // Check if this is a package or group event (for tag display)
    const isPackage = item.type === "package"
    const isGroupEvent = item.type === "group_event"

    return (
      <div
        key={index}
        className={twMerge("relative w-full", getThemeShapeClasses(theme))}
        style={{ border: `1px solid ${theme?.colors?.stroke || "#E6E6E6"}` }}>
        <div className="relative flex w-full flex-row items-center justify-start gap-8 px-10 py-8">
          {/* Photo + Content */}
          <div className="relative flex min-h-px flex-1 flex-row items-center justify-start gap-8 p-0">
            {/* Large Square Image */}
            <div className={twMerge("h-48 w-48 shrink-0 overflow-hidden bg-gray-100", getThemeShapeClasses(theme))}>
              <img
                src={imageUrl}
                alt={item.name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
            </div>

            {/* Content */}
            <div className="relative flex min-h-px flex-1 flex-col items-start justify-start gap-2 overflow-hidden p-0 text-left">
              {/* Price + Title */}
              <div className="relative flex w-full flex-col items-start justify-start p-0">
                {/* Price and Tag */}
                <div className="flex items-center gap-1.5">
                  {price && (
                    <div
                      className="relative text-[20px]"
                      style={{
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                        fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                        color: headingColor,
                        lineHeight: "32px"
                      }}>
                      {price}
                    </div>
                  )}
                  {(isPackage || isGroupEvent) && (
                    <div
                      className="rounded px-3 py-1"
                      style={{
                        backgroundColor: theme?.colors?.bgLight || themeColors.bgLight || "#F4ECE9",
                        borderRadius: theme?.borderRadius ? `${parseInt(theme.borderRadius) / 4}px` : "4px"
                      }}>
                      <span
                        className="text-xs font-semibold uppercase tracking-wide"
                        style={{
                          fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                          fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                          color: headingColor,
                          lineHeight: "16px"
                        }}>
                        {isPackage ? "4 Session package" : "Group Event"}
                      </span>
                    </div>
                  )}
                </div>
                {/* Title */}
                <div
                  className="relative line-clamp-2 w-full text-[28px]"
                  style={{
                    fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                    fontWeight: theme?.fonts?.headingWeight || "700",
                    color: headingColor,
                    lineHeight: "40px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}>
                  {item.name}
                </div>
              </div>

              {/* Duration and Location */}
              {(duration || locationInfo) && (
                <div
                  className="relative w-full truncate text-[16px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor,
                    lineHeight: "24px"
                  }}>
                  {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
                </div>
              )}

              {/* Description */}
              {displayDescription && (
                <div
                  className="prose prose-lg relative line-clamp-2 w-full max-w-none text-[18px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: bodyColor,
                    lineHeight: "32px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}
                  dangerouslySetInnerHTML={{ __html: displayDescription }}
                />
              )}
            </div>
          </div>

          {/* Button */}
          <button
            onClick={() => handleServiceClick(item.id)}
            className="relative inline-flex shrink-0 cursor-pointer flex-row items-center justify-center gap-3 border-2 px-12 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: "#FFFFFF",
                borderColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.primary || "#BD654F",
                fontSize: "16px",
                lineHeight: "24px"
              },
              theme
            )}>
            Details
          </button>
        </div>
      </div>
    )
  }

  // Render list layout (horizontal row design)
  const renderListLayout = () => (
    <div className="flex w-full flex-col gap-10">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards - Horizontal Rows */}
      <div className="flex flex-col gap-6">{allItems.map((item, index) => renderListServiceCard(item, index))}</div>
    </div>
  )

  // Render highlights layout
  const renderHighlightsLayout = () => (
    <div className="flex w-full flex-col gap-10">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards Grid */}
      <div className={layout === "highlights_2" ? "grid grid-cols-2 gap-10" : "flex flex-row gap-10"}>
        {allItems.map((item, index) => renderHighlightCard(item, index))}
      </div>
    </div>
  )

  const isListLayout = layout === "list"
  const isDoubleImageLayout = layout === "double_img_r" || layout === "double_img_l"
  const isHighlightsLayout = layout === "highlights_2" || layout === "highlights_3"
  const isImagesOnRight = isDoubleImageLayout && layout === "double_img_r"

  return (
    <section
      className={twMerge("relative py-24", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <AnimatedSection className={twMerge("mx-auto max-w-[1160px]", !isImagesOnRight ? "px-14" : "px-6")}>
        {isListLayout
          ? renderListLayout()
          : isDoubleImageLayout
          ? renderDoubleImageLayout(layout === "double_img_r")
          : isHighlightsLayout
          ? renderHighlightsLayout()
          : renderDoubleImageLayout(true)}
      </AnimatedSection>
    </section>
  )
}

export default FeaturedServicesSectionDesktop
